"use client"

import { cn } from "@/lib/utils"
import { PARTNER_TEASER_VISUALS, type PartnerTeaserItem } from "@/lib/partner-teaser-brands"

type PartnerTeaserGridProps = {
  items: PartnerTeaserItem[]
  title: string
  viewAllLabel: string
  className?: string
}

/**
 * Klarna-style 3×3 partner teaser grid. Logos and brand names stay blurred;
 * deal lines read clearly in green beneath each tile.
 */
export function PartnerTeaserGrid({ items, title, viewAllLabel, className }: PartnerTeaserGridProps) {
  const gridItems = items.slice(0, 9)

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border/70 bg-white shadow-sm dark:bg-card dark:shadow-none",
        className,
      )}
    >
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3.5 sm:px-5">
        <h3 className="text-[15px] font-semibold tracking-tight text-foreground sm:text-base">{title}</h3>
        <span className="text-[13px] font-medium text-violet-600 dark:text-violet-400">{viewAllLabel}</span>
      </div>

      <div className="grid grid-cols-3">
        {gridItems.map((item, index) => {
          const visual = PARTNER_TEASER_VISUALS[item.id]
          const col = index % 3
          const row = Math.floor(index / 3)

          return (
            <div
              key={item.id}
              className={cn(
                "flex min-h-[104px] flex-col items-center justify-center px-2 py-4 text-center sm:min-h-[112px] sm:py-5",
                col < 2 && "border-r border-border/50",
                row < 2 && "border-b border-border/50",
              )}
              aria-hidden
            >
              <div
                className={cn(
                  "flex h-11 w-11 items-center justify-center rounded-full blur-[5px] select-none sm:h-12 sm:w-12",
                  visual?.bg ?? "bg-muted",
                )}
              >
                <span className={cn("text-[10px] font-bold tracking-tight", visual?.text ?? "text-foreground")}>
                  {visual?.initials ?? item.partnerName.slice(0, 2).toUpperCase()}
                </span>
              </div>
              <p className="mt-2.5 max-w-full line-clamp-1 blur-[4px] select-none text-[12px] font-semibold text-foreground sm:text-[13px]">
                {item.partnerName}
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
